import { get, isEmpty, isNil, set } from 'lodash';
import { RSAA, RSAAAction, RSAACall } from 'redux-api-middleware';
import { setTokensFromStorage } from 'store/auth/authActions';
import { resetAuthTokens, setAuthTokens } from 'utilities/authTokens';

// Lazy store access to avoid circular dependency:
// createAPIAction -> createStore -> reducers -> reducer -> actions -> createAPIAction
const getStore = () => require('store/createStore').store; // eslint-disable-line

// Inline selectors to avoid circular dependency:
// createAPIAction -> selectors -> reducers -> reducer -> actions -> createAPIAction
const selectAccessTokenFromState = (state: any): string | undefined =>
  get(state, 'auth.tokens.accessToken');
const selectRefreshTokenFromState = (state: any): string | null =>
  get(state, 'auth.tokens.refreshToken', null);

const baseUrl = '/api';

/**
 * returns array of action names to use them as default types for RSAA
 */
export const getActionTypes = (
  actionName: string
): [string, string, string] => [
  `${actionName}/REQUEST`,
  `${actionName}/SUCCESS`,
  `${actionName}/FAIL`,
];

const convertParamsToUrl = (params: any) => {
  const urlParams = new URLSearchParams(params);
  return !isEmpty(params) ? `?${urlParams.toString()}` : '';
};

const getAccessHeader = (token?: string, isFormData = false) => {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (!isFormData) {
    headers.Accept = 'application/json';
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

const getDefaultFetch =
  (state: any, signal: any) =>
  async (...fetchArgs: any[]) => {
    const [url, options] = fetchArgs;
    if (signal) {
      options.signal = signal;
    }
    const res = await window.fetch(url, options);

    if (res && res.status === 401) {
      const refreshToken = selectRefreshTokenFromState(state);
      if (isNil(refreshToken)) {
        // No refresh token (SSO flow) — clear stale tokens and redirect to MSAL auth
        resetAuthTokens();
        const currentPath = `${window.location.pathname}${window.location.search}`;
        const encoded = encodeURIComponent(currentPath);
        window.location.href = `/auth?redirect=${encoded}`;
        return res;
      }

      try {
        const rawResponse = await window.fetch(
          `${baseUrl}/isp-service/refresh-token`,
          {
            method: 'PUT',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
          }
        );
        const response = await rawResponse.json();

        // update Cookies and Redux if we have new tokens
        if (response) {
          setAuthTokens({ payload: response });
          getStore().dispatch(setTokensFromStorage(response));
          // updating options to send return request immediately
          set(
            options,
            'headers.Authorization',
            `Bearer ${response.accessToken}`
          );
        }

        const retryRes = await window.fetch(url, options);

        return retryRes;
      } catch (e) {
        return res;
      }
    }

    return res;
  };

export interface IAPIAction<Arguments extends any[], S, P, M> {
  (...args: Arguments): RSAAAction<S, P, M>;
  type: string;
  typeAPIRequest: string;
  typeAPISuccess: string;
  typeAPIFail: string;
}

interface IRSAACallCustom extends RSAACall {
  data: Record<string, any> | FormData;
  params: any;
  headers?: Record<string, string>;
}

export interface ISetupAction<T extends any[]> {
  (...args: T): Partial<IRSAACallCustom>;
}

const createAPIAction = <Arguments extends any[], S = any, P = any, M = any>(
  type: string,
  setupAction: ISetupAction<Arguments>
): IAPIAction<Arguments, S, P, M> => {
  const actionCreator = (...args: Arguments) => {
    const { endpoint, method, data, fetch, params, headers: customHeaders } = setupAction(...args);

    const signal = get(data, 'signal', null);

    const state = getStore().getState();
    const accessToken = selectAccessTokenFromState(state);
    const defaultFetch = getDefaultFetch(state, signal);
    const endpointBaseUrl =
      type.indexOf('DOWNLOAD_') > -1 || type.indexOf('UPLOAD_') > -1
        ? ''
        : baseUrl;

    const action: RSAAAction<S, P, M> = {
      [RSAA]: {
        endpoint: `${endpointBaseUrl}${endpoint}${convertParamsToUrl(params)}`,
        method: method || 'GET',
        types: getActionTypes(type),
        headers: {
          ...getAccessHeader(accessToken, data instanceof window.FormData),
          ...customHeaders,
        },
        fetch: defaultFetch,
      },
    };

    if (fetch) {
      action[RSAA].fetch = fetch;
    }

    if (data) {
      if (data instanceof FormData) {
        action[RSAA].body = data;
      } else {
        const { signal, ...requestData } = data; // eslint-disable-line
        action[RSAA].body = JSON.stringify(requestData);
      }
    }

    return action;
  };

  actionCreator.type = type;
  actionCreator.typeAPIRequest = `${type}/REQUEST`;
  actionCreator.typeAPISuccess = `${type}/SUCCESS`;
  actionCreator.typeAPIFail = `${type}/FAIL`;
  return actionCreator;
};

export default createAPIAction;
